<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { ListItemModel } from '../_runtime/services/UnitMaterialsService';
import type { FormModel } from '../../materials/_runtime/services/MaterialsService';
import MaterialForm from './MaterialForm.vue';

// Props
interface Props
{
    element: ListItemModel;
    edit: ListItemModel;
    unsave: boolean;
    unitOriginId?: number;
    filter: ModulesKey;
    menu: (el: any) => any;
}

const props = withDefaults(defineProps<Props>(), {
    unitOriginId: 0
});

const { $t } = useLocalization();
const { $alert } = useAlerts();
const { $events } = useEvents();

// Emits
const emit = defineEmits<{
    unitMaterialUpdate: [updateEvent: { previous: ListItemModel; current: ListItemModel }];
    materialUpdate: [updateEvent: ListItemModel];
    onExpand: [value: boolean];
    changesOnSlide: [item: ListItemModel];
}>();

// Reactive state
const isDraggable = ref(true);
const isExpanded = ref(false);
const isInitiated = ref(false);
const hover = ref(false);
const contextMenu = ref(false);
const editPosition = ref(false);
const menuItems = ref<any[]>([]);

// Computed properties
const elementProxy = computed({
    get: () => props.element,
    set: (element: ListItemModel) =>
    {
        onUnitMaterialUpdate(element);
    }
});

const target = computed((): string =>
{
    return `material-menu-${props.element.id}`;
});

const container = computed((): string =>
{
    return `material-container-${props.element.id}`;
});

const hasMenu = computed((): boolean =>
{
    return props.menu != null;
});

// Methods
function onClick(): void
{
    if (!isInitiated.value)
        isInitiated.value = true;

    isExpanded.value = !isExpanded.value;
    onExpand(isExpanded.value);
}

async function getMenu(element: ListItemModel): Promise<any[]>
{
    if (props.menu)
        return await props.menu(element);
    else
        return [];
}

function onMenuItem(element: ListItemModel, item: any, confirmed: boolean): void
{
    if (confirmed)
    {
        $events.$emit('material::popover::hide', element);
        togglePopover();
        item.click(element);
    }
}

async function onMenuItemIcon(node: ListItemModel, item: any, confirmed: boolean): Promise<void>
{
    if (confirmed)
    {
        item.click(node);
    }
}

function menuItemId(index: number): string
{
    return `dropdown-${props.element.id}-item-${index}`;
}

function menuItemIcon(index: number, element: number): string
{
    const position = props.element.position;

    return `dropdown-${position}-item-${index}-${element}`;
}

function icon(icon: string): string
{
    return icon;
}

function mouseOver(event: MouseEvent): void
{
    hover.value = true;
}

function mouseOut(event: MouseEvent): void
{
    if (contextMenu.value == false)
    {
        hover.value = false;
    }
}

async function togglePopover(): Promise<void>
{
    if (contextMenu.value == false)
    {
        $events.$emit('material::popover::hide', props.element);
    }

    contextMenu.value = !contextMenu.value;

    if (contextMenu.value == true)
    {
        getMenuItems();
    }
}

async function getMenuItems(): Promise<void>
{
    menuItems.value = await getMenu(props.element);

    if (menuItems.value.length == 0)
    {
        $alert.info($t('[[[Ten element nie zawiera menu kontekstowego.]]]'));
        contextMenu.value = false;
    }

    // check unsave materials
    const values = getEditIdElements();

    if (values && values.length > 0)
    {
        for (let i = 0; i < values.length; i++)
        {
            if (values[i] === props.element.materialId)
                editPosition.value = true;
        }
    }
}

function hidePopover(node: ListItemModel): void
{
    if (node === null || node != props.element)
    {
        menuItems.value = [];
        contextMenu.value = false;
        hover.value = false;
    }
}

function materialTypeText(type: string): string
{
    const text = MaterialTypeOptions().find(p => p.value === type)?.text;

    return text;
}

function onUnitMaterialUpdate(item: ListItemModel): { previous: ListItemModel; current: ListItemModel }
{
    const updateEvent = {
        previous: props.element,
        current: item
    };

    emit('unitMaterialUpdate', updateEvent);

    return updateEvent;
}

function onMaterialUpdate(updateEvent: ListItemModel): ListItemModel
{
    if (updateEvent.materialId === props.element.materialId)
    {
        editPosition.value = false;
        clearNoSaveElement(updateEvent.materialId);
    }

    emit('materialUpdate', updateEvent);

    return updateEvent;
}

function onExpand(value: boolean): boolean
{
    emit('onExpand', value);

    return value;
}

function onChangesOnSlide(item: ListItemModel): boolean
{
    if (item.materialId === props.element.materialId)
    {
        editPosition.value = true;
        noSaveElement(editPosition.value, item.materialId);
    }

    emit('changesOnSlide', item);

    return true;
}

function changeForm(value: FormModel)
{
    if (value.id)
        localStorage.setItem(`content-no-save-${value.id}`, JSON.stringify(value));
}

function noSaveElement(value: boolean, id: number): void
{
    if (id)
        localStorage.setItem(`no-save-${id}`, value.toString());
}

function clearNoSaveElement(id: number): void
{
    localStorage.removeItem(`no-save-${id}`);
    localStorage.removeItem(`content-no-save-${id}`);
}

// Lifecycle hooks
onMounted(() =>
{
    $events.$on('material::popover::hide', hidePopover);
    getMenuItems();
});

onUnmounted(() =>
{
    $events.$off('material::popover::hide', hidePopover);
});
</script>

<template>
    <div
        v-if="element"
        :id="container"
        class="page-component"
        :class="isDraggable ? 'draggable' : ''"
        @mouseover.stop="mouseOver"
        @mouseout.stop="mouseOut"
    >
        <div class="header">
            <span class="d-flex align-items-baseline w-100 material-item">
                <span class="box" @click="onClick">
                    <i v-if="isExpanded" class="fal fa-fw fa-minus" />
                    <i v-else class="fal fa-fw fa-plus" />
                </span>
                <i v-if="(unsave && element.materialId === 0) || element.materialId === 0 || editPosition" class="fa fa-fw fa-circle text-danger" />
                <i v-else-if="element.isPublished" class="fa fa-fw fa-circle text-success" />
                <i v-else-if="!element.isPublished" class="fa fa-fw fa-circle text-secondary" />
                <span :class="['flex-fill material-title', { 'unsave': (unsave && element.materialId === 0) || element.materialId === 0 || editPosition }]" @click="onClick">
                    <span>{{ element.materialId }}&nbsp;-&nbsp;</span>
                    <span>{{ element.name }}</span>
                    <span>&nbsp;({{ materialTypeText(element.type) }})</span>
                </span>
                <i v-show="hover && mobile" v-if="hasMenu" :id="target" class="fa fa-fw fa-cog" @click.stop.prevent="togglePopover" />
                <template v-if="!mobile && hasMenu">
                    <template v-for="(item, index) in menuItems" :key="`icon-${index}`">
                        <i :id="menuItemIcon(index, element.id)" :class="item.icon" :title="item.name" @click="onMenuItemIcon(element, item, !item.confirm)" />
                        <Confirmation
                            v-if="item.confirm"
                            :key="`confirmation-${index}`"
                            placement="bottom"
                            :target="menuItemIcon(index, element.id)"
                            :message="item.confirm"
                            :value="item"
                            @confirm="onMenuItemIcon(element, item, true)"
                        />
                    </template>
                </template>
            </span>
        </div>
        <IdeoPopover :show="mobile && contextMenu" :target="target" :container="container" placement="bottomright">
            <div class="dropdown">
                <template v-for="(item, index) in menuItems">
                    <div v-if="item.divider" :key="`divider-${index}`" class="dropdown-divider">
                        {{ index }}
                    </div>
                    <button
                        v-else
                        :id="menuItemId(index)"
                        :key="`button-${index}`"
                        type="button"
                        class="dropdown-item"
                        :class="item.class"
                        @click="onMenuItem(element, item, !item.confirm)"
                    >
                        <i class="me-2" :class="item.icon" /> {{ item.name }}
                        <Confirmation v-if="item.confirm" :target="menuItemId(index)" :message="item.confirm" :value="item" @confirm="onMenuItem(element, item, true)" />
                    </button>
                </template>
            </div>
        </IdeoPopover>
        <Transition name="fade">
            <div v-if="isInitiated" v-show="isExpanded">
                <MaterialForm
                    v-model="elementProxy"
                    :filter="filter"
                    :unit-origin-id="unitOriginId"
                    @mouseover.stop="isDraggable=false"
                    @mouseout.stop="isDraggable=true"
                    @material-update="onMaterialUpdate"
                    @changes-on-slide="onChangesOnSlide(element)"
                    @change-form="changeForm"
                />
            </div>
        </Transition>
    </div>
</template>

<style lang="scss" scoped>
.page-component {
    position: relative;
    display: inline-block;
    width: 100%;
    margin-bottom: 2px;
    background: var(--course-program);
    border-bottom: 1px solid var(--bs-border-color);

    &.selected {
        border-color: var(--bs-success);
        box-shadow: 3px 3px 0 0 rgba(var(--bs-success), 0.15);
    }

    .header {
        padding: 15px 10px;
        cursor: pointer;
    }

    .footer {
        padding: 5px 10px 10px;
    }

    .description {
        white-space: pre-wrap;
        word-wrap: break-word;
        text-align: justify;
    }
}

.popover{
    left: unset!important;
    right: 10px!important;
    top: 34px!important;
    transform: none!important;
}

.material-item {
    gap: 10px;

    .fad::after {
        opacity: 1;
    }

    .material-title {
        font-size: .875rem;
        font-weight: 600;
    }

    i.fa {
        font-size: 1rem;
    }
}

.box {
    background-color: #E1E2E3;
    border-radius: 4px;
    padding: 4px 5px;
    cursor: pointer;

    & > i {
        font-size: .75rem;
        color: #646464;
        display: block;
    }
}

.unsave {
    color: red;
}
</style>
